import mixpanel from 'mixpanel-browser';
import env from '../config/config';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import config from 'config/config';
import { authService } from 'services/AuthProvider';
import { AuthServiceImpl } from 'services/auth-service';
import { getAnalyticsSessionContext } from './analyticsSessionContext';
import {
  CUSTOM_DATE_RANGE_SUBMIT_ELEMENT_ID,
  PRODUCTION_HOURS_APPLY_MODE_TOGGLE_ELEMENT_ID,
  PRODUCTION_HOURS_START_DATE_PICKER_ELEMENT_ID,
  PRODUCTION_HOURS_VIEWING_SYSTEM_TYPE_SELECT_ELEMENT_ID,
  PRODUCTION_HOURS_VIEWING_SITE_SELECT_ELEMENT_ID,
  PRODUCTION_HOURS_SLOT_EDITOR_APPLY_BUTTON_ELEMENT_ID,
  PRODUCTION_HOURS_SLOT_EDITOR_CANCEL_BUTTON_ELEMENT_ID,
  PRODUCTION_HOURS_SLOT_EDITOR_APPLY_DAYS_ELEMENT_ID,
  PRODUCTION_HOURS_DAY_ROW_SAVE_BUTTON_ELEMENT_ID,
  PRODUCTION_HOURS_DAY_ROW_CANCEL_BUTTON_ELEMENT_ID,
  PRODUCTION_HOURS_SAVE_CHANGES_BUTTON_ELEMENT_ID,
  PRODUCTION_HOURS_DISCARD_BUTTON_ELEMENT_ID,
  PRODUCTION_HOURS_MANAGE_HOLIDAYS_BUTTON_ELEMENT_ID,
  PRODUCTION_HOURS_HOLIDAYS_CALENDAR_CELL_ELEMENT_ID,
  PRODUCTION_HOURS_HOLIDAYS_SAVE_BUTTON_ELEMENT_ID,
  PRODUCTION_HOURS_HOLIDAYS_CLOSE_BUTTON_ELEMENT_ID,
  PRODUCTION_HOURS_HOLIDAYS_DELETE_REQUEST_BUTTON_ELEMENT_ID,
  PRODUCTION_HOURS_HOLIDAYS_DELETE_CONFIRM_BUTTON_ELEMENT_ID,
  PRODUCTION_HOURS_HOLIDAYS_DELETE_CANCEL_BUTTON_ELEMENT_ID,
} from './analyticsElementIds';
export const appInsightLogger = new ApplicationInsights({
  config: {
    instrumentationKey: config.LOGGER_INSTRUMENTAION_KEY,
  },
});
appInsightLogger.loadAppInsights();

const mixPannelKey = env.MIXPANEL_KEY;
const EVENT_SCHEMA_VERSION = 1;
const LOGIN_LIFECYCLE_SESSION_STORAGE_KEY = 'konnect_mixpanel_login_lifecycle';

mixpanel.init(mixPannelKey);

type AnalyticsProperties = Record<string, unknown>;

type AnalyticsUserContext = {
  user_email?: string;
  tenant_id?: string;
  tenant_name?: string;
  account_type?: string | null;
  office?: string | null;
  user_system_groups?: string[];
};

type AnalyticsRouteLocation = {
  pathname?: string;
  search?: string;
  hash?: string;
};

type AnalyticsRouteContext = {
  route_path: string;
  route_normalized_path: string;
  route_search: string;
  route_search_keys: string[];
  route_hash: string;
  route_url: string;
};

let analyticsUserContext: AnalyticsUserContext = {};
let lastTrackedRouteContext: AnalyticsRouteContext | undefined;
const chartTooltipViewTimestamps: Record<string, number> = {};
const CHART_TOOLTIP_DEDUP_WINDOW_MS = 1500;
const trackedLoginLifecycleKeys = new Set<string>();

type ChartTooltipViewedArgs = {
  chart_id: string;
  element_id: string;
  interaction_type: 'hover' | 'focus' | 'tap';
  tooltip_series?: string;
  tooltip_label?: string;
  tooltip_value_bucket?: string;
  report_name?: string;
  selected_system_group?: string;
  timeframe?: string;
};

const setAnalyticsUserContext = (context: AnalyticsUserContext) => {
  const definedContext = Object.entries(context).reduce<AnalyticsUserContext>(
    (result, [key, value]) => (value === undefined ? result : { ...result, [key]: value }),
    {}
  );

  analyticsUserContext = {
    ...analyticsUserContext,
    ...definedContext,
  };
};

const getClaim = (claimName: string): unknown => {
  const idTokenPayload = authService.idTokenPayload || {};
  return idTokenPayload[`${config.AUTH0_CLAIMS_NAMESPACE}${claimName}`];
};

const getStringValue = (value: unknown): string | undefined => {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

const getRouteSearchKeys = (search: string): string[] => {
  return Array.from(new URLSearchParams(search).keys());
};

const normalizeRoutePath = (pathname: string): string => {
  return pathname
    .replace(/\/[0-9]+(?=\/|$)/g, '/:id')
    .replace(/\/[0-9a-fA-F-]{8,}(?=\/|$)/g, '/:id');
};

const getRouteKey = (routeContext: AnalyticsRouteContext): string => {
  return `${routeContext.route_path}${routeContext.route_search}${routeContext.route_hash}`;
};

const getRouteContext = (location?: AnalyticsRouteLocation): AnalyticsRouteContext => {
  const pathname = location?.pathname || window.location.pathname;
  const search = location?.search || window.location.search;
  const hash = location?.hash || window.location.hash;

  return {
    route_path: pathname,
    route_normalized_path: normalizeRoutePath(pathname),
    route_search: search,
    route_search_keys: getRouteSearchKeys(search),
    route_hash: hash,
    route_url: `${window.location.origin}${pathname}${search}${hash}`,
  };
};

const getIsSuperUser = (authService: AuthServiceImpl): boolean => {
  return Boolean(authService.idTokenPayload[`${config.AUTH0_CLAIMS_NAMESPACE}is_super_user`]);
};

const getCommonEventProperties = (): AnalyticsProperties => {
  const idTokenPayload = authService.idTokenPayload || {};
  const { pathname, search, href } = window.location;
  const userEmail = getUserEmail(authService);
  const sessionContext = getAnalyticsSessionContext();

  return {
    event_schema_version: EVENT_SCHEMA_VERSION,
    occurred_at: new Date().toISOString(),
    ...sessionContext,
    user_id: getStringValue(idTokenPayload.sub),
    user_email: getStringValue(userEmail) || analyticsUserContext.user_email,
    is_super_user: getIsSuperUser(authService),
    user_roles: getClaim('roles'),
    tenant_id: analyticsUserContext.tenant_id,
    tenant_name: analyticsUserContext.tenant_name,
    account_type: analyticsUserContext.account_type,
    office: analyticsUserContext.office,
    user_system_groups: analyticsUserContext.user_system_groups,
    env_name: config.ENV_NAME,
    platform: config.PLATFORM,
    route_path: pathname,
    route_search: search,
    route_search_keys: getRouteSearchKeys(search),
    route_url: href,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
  };
};

const trackEvent = (eventName: string, properties: AnalyticsProperties = {}) => {
  if (mixPannelKey) {
    mixpanel.track(eventName, {
      ...properties,
      ...getCommonEventProperties(),
    });
  }
};

const shouldTrackChartTooltipView = (props: ChartTooltipViewedArgs): boolean => {
  const tooltipKey = [props.chart_id, props.tooltip_label || '', props.tooltip_series || ''].join(
    '|'
  );
  const now = Date.now();
  const lastTrackedAt = chartTooltipViewTimestamps[tooltipKey];

  if (lastTrackedAt && now - lastTrackedAt < CHART_TOOLTIP_DEDUP_WINDOW_MS) {
    return false;
  }

  chartTooltipViewTimestamps[tooltipKey] = now;
  return true;
};

const shouldTrackLoginLifecycle = (user): boolean => {
  try {
    const sessionContext = getAnalyticsSessionContext();
    const lifecycleKey = [
      sessionContext.session_id,
      user.email || '',
      user.importedTenantId || '',
    ].join('|');

    if (trackedLoginLifecycleKeys.has(lifecycleKey)) {
      return false;
    }

    if (window.sessionStorage.getItem(LOGIN_LIFECYCLE_SESSION_STORAGE_KEY) === lifecycleKey) {
      trackedLoginLifecycleKeys.add(lifecycleKey);
      return false;
    }

    window.sessionStorage.setItem(LOGIN_LIFECYCLE_SESSION_STORAGE_KEY, lifecycleKey);
    trackedLoginLifecycleKeys.add(lifecycleKey);
    return true;
  } catch {
    // Lifecycle dedupe must never block identity sync or event tracking.
    return true;
  }
};

const actions = {
  login: user => {
    const shouldTrackLifecycle = shouldTrackLoginLifecycle(user);

    setAnalyticsUserContext({
      user_email: user.email,
      tenant_id: user.importedTenantId,
      tenant_name: user.tenantName,
      office: user.office,
      account_type: user.accountType,
      user_system_groups: user.userSystemGroup,
    });

    if (mixPannelKey) {
      mixpanel.identify(user.email);
      //*register action is binding income properties to every action
      mixpanel.register({
        isSuperUser: checkIsSuperUser(authService),
      });
      if (shouldTrackLifecycle) {
        trackEvent('user_authenticated', {
          event_type: 'lifecycle',
        });
        trackEvent('session_started', {
          event_type: 'lifecycle',
          reason: 'user entered in app',
        });
      }
      //*sets registration date - works only once - first enter
      mixpanel.people.set_once({
        $email: user.email,
        'Sign up date': new Date().toISOString(),
      });
      //*all of parameters should be in func above   (set_once), but there was issue that some of them comes as undefined from time to time form server side. that's why they were moved to method - set()
      mixpanel.people.set({
        USER_ID: user.email,
        importedTenantId: user.importedTenantId,
        tenantName: user.tenantName,
        office: user.office,
        accountType: user.accountType,
        userSystemGroup: user.userSystemGroup.join(' / '),
        isSuperUser: checkIsSuperUser(authService),
      });
    }

    if (shouldTrackLifecycle) {
      appInsightLogger.trackEvent(
        { name: 'Login' },
        { isSuperUser: checkIsSuperUser(authService), email: getUserEmail(authService) }
      );
      appInsightLogger.trackEvent(
        { name: 'StartSession' },
        {
          reason: 'user entered in app',
          isSuperUser: checkIsSuperUser(authService),
          email: getUserEmail(authService),
        }
      );
    }
    //@ts-ignore
    window.pendo.initialize({
      visitor: {
        id: user.email, // Required if user is logged in, default creates anonymous ID
        email: user.email, // Recommended if using Pendo Feedback, or NPS Email
        // full_name:    // Recommended if using Pendo Feedback
        // role:         // Optional

        // You can add any additional visitor level key-values here,
        // as long as it's not one of the above reserved names.
        isDtgUser: user.userSystemGroup.includes('DTG'),
        isDtfUser: user.userSystemGroup.includes('DTF'),
        isApolloUser: user.userSystemGroup.includes('Apollo'),
        userSystemGroup: user.userSystemGroup.join(' / '),
      },

      account: {
        id: user.tenantName, // Required if using Pendo Feedback, default uses the value 'ACCOUNT-UNIQUE-ID'
        // name:         // Optional
        // is_paying:    // Recommended if using Pendo Feedback
        // monthly_value:// Recommended if using Pendo Feedback
        // planLevel:    // Optional
        // planPrice:    // Optional
        // creationDate: // Optional

        // You can add any additional account level key-values here,
        // as long as it's not one of the above reserved names.
      },
    });
  },
  startSession: (name, props?) => {
    if (mixPannelKey) {
      trackEvent('session_resumed', {
        event_type: 'lifecycle',
        action: 'Start Session',
        reason: props.reason,
      });
    }
    appInsightLogger.trackEvent(
      { name },
      {
        action: 'Start Session',
        reason: props.reason,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },
  sendErrorLog: (name, error, errorInfo) => {
    if (mixPannelKey) {
      trackEvent('error_logged', {
        event_type: 'diagnostic',
        action: 'Error',
        error: error,
        errorInfo: errorInfo,
      });
    }
    appInsightLogger.trackEvent(
      { name },
      {
        action: 'Error',
        error: error,
        errorInfo: errorInfo,
      }
    );
  },
  finishSession: (name, props?) => {
    if (mixPannelKey) {
      trackEvent('session_ended', {
        event_type: 'lifecycle',
        action: 'End Session',
        // reason: props.reason,
      });
    }
    appInsightLogger.trackEvent(
      { name },
      {
        action: 'End Session',
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
    //@ts-ignore
  },
  changeReportPage: (name, props?) => {
    if (mixPannelKey) {
      trackEvent('report_changed', {
        event_type: 'user_action',
        element_id: props.element_id,
        interaction_type: props.interaction_type,
        action: 'Change',
        prevReport: props.prevReport,
        nextReport: props.nextReport,
      });
    }
    appInsightLogger.trackEvent(
      { name },
      {
        action: 'Change',
        prevReport: props.prevReport,
        nextReport: props.nextReport,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },
  changeTimeFrameFilter: (name, props?) => {
    if (mixPannelKey) {
      trackEvent('timeframe_changed', {
        event_type: 'user_action',
        element_id: props.element_id,
        interaction_type: props.interaction_type,
        action: 'Change',
        nextTimeFilter: props.nextTimeFilter,
        startDate: props.startDate,
        endDate: props.endDate,
        customStartHour: props.customStartHour,
        customEndHour: props.customEndHour,
      });
    }
    appInsightLogger.trackEvent(
      { name },
      {
        action: 'Change',
        nextTimeFilter: props.nextTimeFilter,
        startDate: props.startDate,
        endDate: props.endDate,
        customStartHour: props.customStartHour,
        customEndHour: props.customEndHour,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },
  changeSystemGroupFilter: (name, props) => {
    if (mixPannelKey) {
      trackEvent('system_group_changed', {
        event_type: 'user_action',
        element_id: props.element_id,
        interaction_type: props.interaction_type,
        action: 'Change',
        prevSystemGroup: props.systemGroupFrom,
        nextSystemGroup: props.systemGroupTo,
        currentPage: props.currentActiveReport,
      });
    }
    appInsightLogger.trackEvent(
      { name },
      {
        action: 'Change',
        prevSystemGroup: props.systemGroupFrom,
        nextSystemGroup: props.systemGroupTo,
        currentPage: props.currentActiveReport,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },
  changePrinterNickName: (name, props) => {
    if (mixPannelKey) {
      trackEvent('system_nickname_changed', {
        event_type: 'user_action',
        element_id: props.element_id,
        interaction_type: props.interaction_type,
        action: 'Change',
        serialNumber: props.serialNumber,
        prevNickName: props.prevNickName,
        newNickName: props.newNickName,
      });
    }
    appInsightLogger.trackEvent(
      { name },
      {
        action: 'Change',
        serialNubmer: props.serialNumber,
        prevNickName: props.prevNickName,
        newNickName: props.newNickName,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },
  downloadErrorAndMaintenanceReport: (selectedSystem, props?) => {
    if (mixPannelKey) {
      trackEvent('error_maintenance_report_downloaded', {
        event_type: 'user_action',
        element_id: props?.element_id,
        interaction_type: props?.interaction_type,
        action: 'DownloadFile',
        systemName: selectedSystem,
      });
    }
    appInsightLogger.trackEvent(
      { name: 'DowloadErrorAndMaintenanceReport' },
      {
        action: 'DownloadFile',
        systemName: selectedSystem,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },
  //*timeLine day view toggle view 24 hour / 1 hour
  toggleTimelineDayView: (prevView: string, nextView: string, props?) => {
    if (mixPannelKey) {
      trackEvent('timeline_view_changed', {
        event_type: 'user_action',
        element_id: props?.element_id,
        interaction_type: props?.interaction_type,
        prevView,
        nextView,
      });
    }
    appInsightLogger.trackEvent(
      { name: 'toggleTimelineDayView' },
      {
        prevView,
        nextView,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },
  //*timeline hour view hour seletction
  selectHourTimelineHourView: (prevHour, nextHour, props?) => {
    if (mixPannelKey) {
      trackEvent('timeline_hour_selected', {
        event_type: 'user_action',
        element_id: props?.element_id,
        interaction_type: props?.interaction_type,
        prevHour,
        nextHour,
      });
    }
    appInsightLogger.trackEvent(
      { name: 'selectHourTimelineHourView' },
      {
        prevHour,
        nextHour,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },
  chartTooltipViewed: (props: ChartTooltipViewedArgs) => {
    if (!props.chart_id || !props.element_id || !props.interaction_type) {
      return;
    }

    if (!shouldTrackChartTooltipView(props)) {
      return;
    }

    const eventProperties = {
      event_type: 'user_action',
      chart_id: props.chart_id,
      element_id: props.element_id,
      interaction_type: props.interaction_type,
      tooltip_series: props.tooltip_series,
      tooltip_label: props.tooltip_label,
      tooltip_value_bucket: props.tooltip_value_bucket,
      report_name: props.report_name,
      selected_system_group: props.selected_system_group,
      timeframe: props.timeframe,
    };

    if (mixPannelKey) {
      trackEvent('chart_tooltip_viewed', eventProperties);
    }

    appInsightLogger.trackEvent(
      { name: 'Chart Tooltip Viewed' },
      {
        ...eventProperties,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },
  toggleDatePickerCalendar: (props?) => {
    if (mixPannelKey) {
      trackEvent('date_picker_toggled', {
        event_type: 'user_action',
        element_id: props?.element_id,
        interaction_type: props?.interaction_type,
        state: props?.state,
        action: 'Open/Close Date Picker Calendar',
      });
    }
    appInsightLogger.trackEvent(
      { name: 'Open/Close Date Picker Calendar' },
      {
        action: 'Open/Close Date Picker Calendar',
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },
  incrementDateInDatePicker: (props?) => {
    if (mixPannelKey) {
      trackEvent('date_picker_date_incremented', {
        event_type: 'user_action',
        element_id: props?.element_id,
        interaction_type: props?.interaction_type,
        action: 'Increment date',
      });
    }
    appInsightLogger.trackEvent(
      { name: 'ncrement date in Date Picker Calendar' },
      {
        action: 'Increment date',
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },
  decrementDateInDatePicker: (props?) => {
    if (mixPannelKey) {
      trackEvent('date_picker_date_decremented', {
        event_type: 'user_action',
        element_id: props?.element_id,
        interaction_type: props?.interaction_type,
        action: 'Decrement date',
      });
    }
    appInsightLogger.trackEvent(
      { name: 'Decrement date in Date Picker Calendar' },
      {
        action: 'Decrement date',
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },
  toggleMachineFilterMenu: (props?) => {
    if (mixPannelKey) {
      trackEvent('machine_filter_toggled', {
        event_type: 'user_action',
        element_id: props?.element_id,
        interaction_type: props?.interaction_type,
        state: props?.state,
        action: 'Open/Close Machine Filter Menu',
      });
    }
    appInsightLogger.trackEvent(
      { name: 'Open/Close MachineFilterMenu' },
      {
        action: 'Open/Close Machine Filter Menu',
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },
  JobAnalysisFilter: (props: JobAnalysisFilterArgs) => {
    const { systemGroup, actionType } = props;

    if (mixPannelKey) {
      trackEvent('job_analysis_filter_changed', {
        event_type: 'user_action',
        element_id: props.element_id,
        interaction_type: props.interaction_type,
        systemGroup,
        actionType,
      });
    }
    appInsightLogger.trackEvent(
      { name: 'Job Analysis Filter' },
      {
        systemGroup,
        actionType,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },
  //*profiles actions
  people: {
    set: props => {
      setAnalyticsUserContext({
        tenant_id: getStringValue(props.importedTenantId),
        tenant_name: getStringValue(props.tenantName),
        office: props.office,
        account_type: props.accountType,
        user_system_groups:
          typeof props.userSystemGroup === 'string'
            ? props.userSystemGroup.split(' / ').filter(Boolean)
            : undefined,
      });

      if (mixPannelKey) mixpanel.people.set(props);
    },
  },
  clickInkConsupmtionPerJob: (timeframe, props?) => {
    if (mixPannelKey) {
      trackEvent('ink_consumption_per_job_clicked', {
        event_type: 'user_action',
        element_id: props?.element_id,
        interaction_type: props?.interaction_type,
        action: 'User clicked on button',
        timeframe,
      });
    }
    appInsightLogger.trackEvent(
      { name: 'Click Ink Consumption Per Job' },
      {
        action: 'User clicked on button',
        timeframe,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },
  changeDateRangeInkConsuptionPerJob: ({
    timeframe,
    startDate,
    endDate,
    newStartDate,
    newEndDate,
  }) => {
    if (mixPannelKey) {
      trackEvent('ink_consumption_date_range_changed', {
        event_type: 'user_action',
        timeframe,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        newStartDate: newStartDate.toISOString(),
        newEndDate: newEndDate.toISOString(),
      });
    }
    appInsightLogger.trackEvent(
      { name: 'Change Date Range Ink Consumption Per Job' },
      {
        timeframe,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        newStartDate: newStartDate.toISOString(),
        newEndDate: newEndDate.toISOString(),
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },
  downloadSystemAnalysisReport: ({ result, element_id, interaction_type }) => {
    if (mixPannelKey) {
      trackEvent('system_analysis_report_downloaded', {
        event_type: 'user_action',
        element_id,
        interaction_type,
        action: 'DownloadFile',

        result,
      });
    }
    appInsightLogger.trackEvent(
      { name: 'Download System Analysis Report' },
      {
        action: 'DownloadFile',

        result,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },
  downloadInkConsumptionPerJobReport: ({ result, element_id, interaction_type }) => {
    if (mixPannelKey) {
      trackEvent('ink_consumption_report_downloaded', {
        event_type: 'user_action',
        element_id,
        interaction_type,
        action: 'DownloadFile',

        result,
      });
    }
    appInsightLogger.trackEvent(
      { name: 'Download Ink Consupmtion Per Job Report' },
      {
        action: 'DownloadFile',
        result,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },
  jobAnalysisInkConsumptionTrendGraphSelectColor: ({ selectedColors, color }) => {
    if (mixPannelKey) {
      trackEvent('ink_consumption_color_selected', {
        event_type: 'user_action',
        action: 'SelectColor',
        selectedColor: color,
      });
    }
    appInsightLogger.trackEvent(
      { name: 'Job Analysis Ink Consumption Trend Color Selection' },
      {
        action: 'SelectColor',
        selectedColor: color,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },

  customClick: ({ selectedSystemGroup, element_id }) => {
    if (mixPannelKey) {
      trackEvent('custom_timeframe_clicked', {
        event_type: 'user_action',
        element_id,
        interaction_type: 'click',
        action: 'Click',
        selectedSystemGroup,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      });
    }
    appInsightLogger.trackEvent(
      { name: 'CustomClick' },
      {
        action: 'Click',
        selectedSystemGroup,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },

  submitCustomClick: ({ startDate, endDate, customStartHour = 0, customEndHour = 0 }) => {
    if (mixPannelKey) {
      trackEvent('custom_date_range_submitted', {
        event_type: 'user_action',
        element_id: CUSTOM_DATE_RANGE_SUBMIT_ELEMENT_ID,
        interaction_type: 'submit',
        action: 'Click',
        nextTimeFilter: 'Custom',
        startDate,
        endDate,
        customStartHour,
        customEndHour,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      });
    }
    appInsightLogger.trackEvent(
      { name: 'SubmitCustomClick' },
      {
        action: 'Click',
        nextTimeFilter: 'Custom',
        startDate,
        endDate,
        customStartHour,
        customEndHour,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },
  changedDateinCustomTimeFrame: ({
    startDate,
    endDate,
    customStartHour = 0,
    customEndHour = 0,
    element_id,
    interaction_type,
  }) => {
    if (mixPannelKey) {
      trackEvent('custom_date_range_changed', {
        event_type: 'user_action',
        element_id,
        interaction_type,
        action: 'Change',
        nextTimeFilter: 'Custom',
        startDate,
        endDate,
        customStartHour,
        customEndHour,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      });
    }
    appInsightLogger.trackEvent(
      { name: 'ChangedDateinCustomTimeFrame' },
      {
        action: 'Change',
        nextTimeFilter: 'Custom',
        startDate,
        endDate,
        customStartHour,
        customEndHour,
        isSuperUser: checkIsSuperUser(authService),
        email: getUserEmail(authService),
      }
    );
  },

  trackHistory: location => {
    const routeContext = getRouteContext(location);
    const routeKey = getRouteKey(routeContext);

    if (lastTrackedRouteContext && getRouteKey(lastTrackedRouteContext) === routeKey) {
      return;
    }

    const previousRouteContext = lastTrackedRouteContext;
    lastTrackedRouteContext = routeContext;

    trackEvent('page_viewed', {
      event_type: 'route',
      ...routeContext,
      previous_route_path: previousRouteContext?.route_path,
      previous_route_normalized_path: previousRouteContext?.route_normalized_path,
      previous_route_search: previousRouteContext?.route_search,
      previous_route_hash: previousRouteContext?.route_hash,
      previous_route_url: previousRouteContext?.route_url,
    });
  },
  workingHoursAddSlot: (props: { dayId: string; slotId: string; site: string }) => {
    trackEvent('working_hours_slot_added', {
      event_type: 'user_action',
      action: 'Add',
      day_id: props.dayId,
      slot_id: props.slotId,
      site: props.site,
    });
  },
  workingHoursRemoveSlot: (props: {
    dayId: string;
    slotId: string;
    site: string;
    // Where the delete was triggered from - the slot bar's own ✕ button, or the Delete
    // button inside the time editor popover. Optional - defaults omitted, not 'bar', so
    // older call sites that don't pass it keep sending the exact same payload as before.
    source?: 'bar' | 'editor';
  }) => {
    trackEvent('working_hours_slot_removed', {
      event_type: 'user_action',
      action: 'Remove',
      day_id: props.dayId,
      slot_id: props.slotId,
      site: props.site,
      source: props.source,
    });
  },
  workingHoursUpdateSlot: (props: {
    dayId: string;
    slotId: string;
    startMinutes: number;
    endMinutes: number;
    site: string;
  }) => {
    trackEvent('working_hours_slot_updated', {
      event_type: 'user_action',
      action: 'Update',
      day_id: props.dayId,
      slot_id: props.slotId,
      start_minutes: props.startMinutes,
      end_minutes: props.endMinutes,
      site: props.site,
    });
  },
  workingHoursToggleDay: (props: { dayId: string; enabled: boolean; site: string }) => {
    trackEvent('working_hours_day_toggled', {
      event_type: 'user_action',
      action: 'Toggle',
      day_id: props.dayId,
      enabled: props.enabled,
      site: props.site,
    });
  },
  //*Production Hours settings page (formerly "Working Hours") - new events use the
  //*production_hours_* prefix to match the page's current name; the events above keep their
  //*original working_hours_* names so existing Mixpanel history/dashboards aren't split.
  productionHoursChangeSite: (props: { siteIds: string[]; elementId: string }) => {
    trackEvent('production_hours_site_changed', {
      event_type: 'user_action',
      element_id: props.elementId,
      interaction_type: 'change',
      action: 'Change',
      site_ids: props.siteIds,
      site_count: props.siteIds.length,
    });
  },
  productionHoursToggleSystemType: (props: {
    systemGroup: string;
    enabled: boolean;
    selectedSystemGroupTypes: string[];
    elementId: string;
  }) => {
    trackEvent('production_hours_system_type_toggled', {
      event_type: 'user_action',
      element_id: props.elementId,
      interaction_type: 'click',
      action: 'Toggle',
      system_group: props.systemGroup,
      enabled: props.enabled,
      selected_system_group_types: props.selectedSystemGroupTypes,
    });
  },
  productionHoursChangeApplyMode: (props: { applyMode: string; startDate: string }) => {
    trackEvent('production_hours_apply_mode_changed', {
      event_type: 'user_action',
      element_id: PRODUCTION_HOURS_APPLY_MODE_TOGGLE_ELEMENT_ID,
      interaction_type: 'click',
      action: 'Change',
      apply_mode: props.applyMode,
      start_date: props.startDate,
    });
  },
  productionHoursChangeStartDate: (props: { startDate: string }) => {
    trackEvent('production_hours_start_date_changed', {
      event_type: 'user_action',
      element_id: PRODUCTION_HOURS_START_DATE_PICKER_ELEMENT_ID,
      interaction_type: 'change',
      action: 'Change',
      start_date: props.startDate,
    });
  },
  productionHoursChangeViewingSystemType: (props: { systemGroup: string }) => {
    trackEvent('production_hours_viewing_system_type_changed', {
      event_type: 'user_action',
      element_id: PRODUCTION_HOURS_VIEWING_SYSTEM_TYPE_SELECT_ELEMENT_ID,
      interaction_type: 'change',
      action: 'Change',
      system_group: props.systemGroup,
    });
  },
  productionHoursChangeViewingSite: (props: { siteId: string }) => {
    trackEvent('production_hours_viewing_site_changed', {
      event_type: 'user_action',
      element_id: PRODUCTION_HOURS_VIEWING_SITE_SELECT_ELEMENT_ID,
      interaction_type: 'change',
      action: 'Change',
      site_id: props.siteId,
    });
  },
  productionHoursApplySlot: (props: {
    dayId: string;
    slotId: string;
    startMinutes: number;
    endMinutes: number;
    overnight: boolean;
    applyDaysCount: number;
    site: string;
  }) => {
    trackEvent('production_hours_slot_applied', {
      event_type: 'user_action',
      element_id: PRODUCTION_HOURS_SLOT_EDITOR_APPLY_BUTTON_ELEMENT_ID,
      interaction_type: 'click',
      action: 'Apply',
      day_id: props.dayId,
      slot_id: props.slotId,
      start_minutes: props.startMinutes,
      end_minutes: props.endMinutes,
      overnight: props.overnight,
      apply_days_count: props.applyDaysCount,
      site: props.site,
    });
  },
  productionHoursCancelSlotEdit: (props: { dayId: string; slotId: string; site: string }) => {
    trackEvent('production_hours_slot_edit_cancelled', {
      event_type: 'user_action',
      element_id: PRODUCTION_HOURS_SLOT_EDITOR_CANCEL_BUTTON_ELEMENT_ID,
      interaction_type: 'click',
      action: 'Cancel',
      day_id: props.dayId,
      slot_id: props.slotId,
      site: props.site,
    });
  },
  productionHoursChangeApplyDays: (props: {
    dayId: string;
    slotId: string;
    applyDays: string[];
    site: string;
  }) => {
    trackEvent('production_hours_apply_days_changed', {
      event_type: 'user_action',
      element_id: PRODUCTION_HOURS_SLOT_EDITOR_APPLY_DAYS_ELEMENT_ID,
      interaction_type: 'click',
      action: 'Change',
      day_id: props.dayId,
      slot_id: props.slotId,
      apply_days: props.applyDays,
      apply_days_count: props.applyDays.length,
      site: props.site,
    });
  },
  productionHoursDayRowAction: (props: {
    action: 'save' | 'cancel';
    dayId: string;
    site: string;
  }) => {
    trackEvent('production_hours_day_row_action', {
      event_type: 'user_action',
      element_id:
        props.action === 'save'
          ? PRODUCTION_HOURS_DAY_ROW_SAVE_BUTTON_ELEMENT_ID
          : PRODUCTION_HOURS_DAY_ROW_CANCEL_BUTTON_ELEMENT_ID,
      interaction_type: 'click',
      action: props.action === 'save' ? 'Save' : 'Cancel',
      day_id: props.dayId,
      site: props.site,
    });
  },
  productionHoursSaveChangesClicked: (props: {
    siteCount: number;
    isMixedSites: boolean;
    isMixedSystemGroupTypes: boolean;
    trigger: 'save_button' | 'slot_applied';
  }) => {
    trackEvent('production_hours_save_changes_clicked', {
      event_type: 'user_action',
      element_id: PRODUCTION_HOURS_SAVE_CHANGES_BUTTON_ELEMENT_ID,
      interaction_type: 'click',
      action: 'Save',
      site_count: props.siteCount,
      is_mixed_sites: props.isMixedSites,
      is_mixed_system_group_types: props.isMixedSystemGroupTypes,
      trigger: props.trigger,
    });
  },
  productionHoursSaveChangesResult: (props: { success: boolean; trigger: 'save_button' | 'slot_applied' }) => {
    trackEvent(
      props.success ? 'production_hours_save_changes_succeeded' : 'production_hours_save_changes_failed',
      {
        event_type: 'user_action',
        element_id: PRODUCTION_HOURS_SAVE_CHANGES_BUTTON_ELEMENT_ID,
        action: 'Save',
        trigger: props.trigger,
      }
    );
  },
  productionHoursDiscardClicked: (props: { site: string }) => {
    trackEvent('production_hours_discard_clicked', {
      event_type: 'user_action',
      element_id: PRODUCTION_HOURS_DISCARD_BUTTON_ELEMENT_ID,
      interaction_type: 'click',
      action: 'Discard',
      site: props.site,
    });
  },
  productionHoursOpenHolidays: (props: { site: string }) => {
    trackEvent('production_hours_holidays_opened', {
      event_type: 'user_action',
      element_id: PRODUCTION_HOURS_MANAGE_HOLIDAYS_BUTTON_ELEMENT_ID,
      interaction_type: 'click',
      action: 'Open',
      site: props.site,
    });
  },
  productionHoursCloseHolidays: (props: { site: string }) => {
    trackEvent('production_hours_holidays_closed', {
      event_type: 'user_action',
      element_id: PRODUCTION_HOURS_HOLIDAYS_CLOSE_BUTTON_ELEMENT_ID,
      interaction_type: 'click',
      action: 'Close',
      site: props.site,
    });
  },
  productionHoursHolidaysDaySelected: (props: { dateKey: string; site: string }) => {
    trackEvent('production_hours_holidays_day_selected', {
      event_type: 'user_action',
      element_id: PRODUCTION_HOURS_HOLIDAYS_CALENDAR_CELL_ELEMENT_ID,
      interaction_type: 'click',
      action: 'Select',
      date_key: props.dateKey,
      site: props.site,
    });
  },
  productionHoursHolidayBlockUpdated: (props: {
    field: 'name' | 'repeat';
    id: string;
    site: string;
  }) => {
    trackEvent('production_hours_holiday_block_updated', {
      event_type: 'user_action',
      interaction_type: 'change',
      action: 'Update',
      field: props.field,
      holiday_id: props.id,
      site: props.site,
    });
  },
  productionHoursHolidaysDeleteRequested: (props: { id: string; site: string }) => {
    trackEvent('production_hours_holiday_delete_requested', {
      event_type: 'user_action',
      element_id: PRODUCTION_HOURS_HOLIDAYS_DELETE_REQUEST_BUTTON_ELEMENT_ID,
      interaction_type: 'click',
      action: 'RequestDelete',
      holiday_id: props.id,
      site: props.site,
    });
  },
  productionHoursHolidaysDeleteConfirmed: (props: { id: string; site: string }) => {
    trackEvent('production_hours_holiday_delete_confirmed', {
      event_type: 'user_action',
      element_id: PRODUCTION_HOURS_HOLIDAYS_DELETE_CONFIRM_BUTTON_ELEMENT_ID,
      interaction_type: 'click',
      action: 'ConfirmDelete',
      holiday_id: props.id,
      site: props.site,
    });
  },
  productionHoursHolidaysDeleteCancelled: (props: { id: string; site: string }) => {
    trackEvent('production_hours_holiday_delete_cancelled', {
      event_type: 'user_action',
      element_id: PRODUCTION_HOURS_HOLIDAYS_DELETE_CANCEL_BUTTON_ELEMENT_ID,
      interaction_type: 'click',
      action: 'CancelDelete',
      holiday_id: props.id,
      site: props.site,
    });
  },
  productionHoursHolidaysSaved: (props: { site: string }) => {
    trackEvent('production_hours_holidays_saved', {
      event_type: 'user_action',
      element_id: PRODUCTION_HOURS_HOLIDAYS_SAVE_BUTTON_ELEMENT_ID,
      interaction_type: 'click',
      action: 'Save',
      site: props.site,
    });
  },
};

type JobAnalysisFilterArgs = {
  systemGroup: string;
  actionType: 'groupByResolution' | 'filter';
  element_id?: string;
  interaction_type?: 'click';
};
export const KonnectAnalytics = actions;

//*helper functions
export const checkIsSuperUser = (authService: AuthServiceImpl): string => {
  return getIsSuperUser(authService) ? 'true' : 'false';
};

export const getUserEmail = (authService: AuthServiceImpl) => {
  return authService.idTokenPayload[`${config.AUTH0_CLAIMS_NAMESPACE}is_super_user`]
    ? authService.idTokenPayload.nickname
    : authService.idTokenPayload.name;
};
